import React from "react";
import {
  Box,
  Container,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Grid,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import { Search, Sort, Close, FilterList } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { categoryConfigs } from "../../utils/offerHelpers";
import { OfferCategory } from "../../types/offers";

interface OffersFilterBarProps {
  showFilterBar: boolean;
  selectedMall: any;
  category: OfferCategory | "all";
  sortBy: "newest" | "expiring" | "discount" | "popular";
  searchQuery: string;
  hasFilters: boolean;
  onCategoryChange: (category: OfferCategory | "all") => void;
  onSortChange: (event: SelectChangeEvent) => void;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onResetFilters: () => void;
}

/**
 * OffersFilterBar Component
 *
 * Filter section with category chips, search input, sort dropdown,
 * and active filters indicator
 */
const OffersFilterBar: React.FC<OffersFilterBarProps> = ({
  showFilterBar,
  selectedMall,
  category,
  sortBy,
  searchQuery,
  hasFilters,
  onCategoryChange,
  onSortChange,
  onSearchChange,
  onResetFilters,
}) => {
  return (
    <AnimatePresence>
      {showFilterBar && selectedMall && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Container maxWidth="lg" sx={{ mt: 4 }}>
            {/* Category Chips - Horizontal scroll on mobile */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 3,
                pb: 2,
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: 2,
                },
              }}
            >
              {Object.values(categoryConfigs).map((config: any) => {
                const Icon = config.icon;
                const isSelected = category === config.key;
                return (
                  <Chip
                    key={config.key}
                    icon={<Icon sx={{ fontSize: 18 }} />}
                    label={config.label}
                    onClick={() => onCategoryChange(config.key)}
                    sx={{
                      background: isSelected ? config.gradient : "white",
                      color: isSelected ? "white" : "text.primary",
                      fontWeight: isSelected ? 600 : 500,
                      fontSize: { xs: "0.875rem", sm: "0.9rem" },
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 2.5, sm: 3 },
                      border: isSelected ? "none" : "1px solid #e0e0e0",
                      boxShadow: isSelected
                        ? "0 4px 12px rgba(0,0,0,0.15)"
                        : "none",
                      transition: "all 0.2s ease",
                      "& .MuiChip-icon": {
                        color: isSelected ? "white" : config.color,
                      },
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                      },
                    }}
                  />
                );
              })}
            </Box>

            {/* Search and Sort */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  placeholder="Search by store name..."
                  value={searchQuery}
                  onChange={onSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search
                          sx={{
                            fontSize: 24,
                            color: "primary.main",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() =>
                            onSearchChange({
                              target: { value: "" },
                            } as React.ChangeEvent<HTMLInputElement>)
                          }
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              color: "error.main",
                              transform: "rotate(90deg)",
                              transition: "all 0.3s ease",
                            },
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: "background.default",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      transition: "all 0.3s ease",
                      "& fieldset": { border: "none" },
                      "&:hover": {
                        backgroundColor: "white",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "white",
                        boxShadow: "0 2px 12px rgba(26, 35, 126, 0.08)",
                        "& .MuiInputAdornment-root svg": {
                          transform: "scale(1.1)",
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <Select
                    value={sortBy}
                    onChange={onSortChange}
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <Sort sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 3,
                      backgroundColor: "white",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="expiring">Expiring Soon</MenuItem>
                    <MenuItem value="discount">Best Discount</MenuItem>
                    <MenuItem value="popular">Most Popular</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Active Filters Indicator */}
            {hasFilters && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  p: 2,
                  background:
                    "linear-gradient(135deg, rgba(26, 35, 126, 0.12) 0%, rgba(57, 73, 171, 0.12) 100%)",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FilterList sx={{ color: "primary.main" }} />
                  <Typography variant="body2" fontWeight={500}>
                    Filters applied
                  </Typography>
                </Box>
                <Chip
                  label="Reset Filters"
                  size="small"
                  onClick={onResetFilters}
                  sx={{
                    backgroundColor: "white",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "primary.main",
                      color: "white",
                    },
                  }}
                />
              </Box>
            )}
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OffersFilterBar;
